/**
 * Modo de vista activo del dashboard. Controla qué set de tabs se renderiza
 * y qué label aparece en el selector del sidebar / mobile nav.
 *
 * - ADMIN: panel de empresa (B2B).
 * - USER: vista del empleado dentro de su empresa (B2B) o del autónomo.
 * - INDIVIDUAL: vista personal/individual (B2C o B2B con plan individual).
 * - RESUMEN: vista agregada de todos sus planes, para quien tiene 2 o más (chirigota#585).
 */
export enum ViewType {
	ADMIN = 'ADMIN',
	USER = 'USER',
	INDIVIDUAL = 'INDIVIDUAL',
	RESUMEN = 'RESUMEN'
}

/**
 * Roles persistidos en `users.roles` (jsonb). Espejo de los valores que
 * el backend lee desde `RolesGuard` con `@Roles('...')`.
 *
 * Son PERMISOS sobre el plan de una empresa. Qué planes tiene una persona no
 * es un rol: lo dice `GET /users/plans`. El antiguo `individual` se ponía por
 * SQL a mano y se retiró el 03-sep-2026 (`resolveAvailableViews` en
 * `utils/viewOptions.ts` lo derivaba de aquí; ahora lo deriva de los planes).
 */
export enum UserRole {
	ADMIN = 'admin',
	USER = 'user',
	MANAGEMENT = 'management'
}

/**
 * Tipo de cuenta derivado por el backend en `User.getAccountType()`.
 * Mismo set de valores que `UserAccountType` en comparsa.
 */
export enum AccountType {
	ADMIN = 'ADMIN',
	USER = 'USER',
	MANAGEMENT = 'MANAGEMENT',
	BOTH = 'BOTH'
}

/**
 * Rutas absolutas del dashboard usadas en `navigateToDefaultTab`.
 * Hardcodeadas a propósito para evitar leer `userTabs[0].route` con
 * `viewType` stale tras un `setViewType`.
 */
export enum DashboardRoute {
	ADMIN_HOME = '/dashboard/home',
	MY_BENEFIT = '/dashboard/my-benefit',
	MY_BENEFIT_INDIVIDUAL = '/dashboard/my-benefit-individual',
	RESUMEN = '/dashboard/resumen'
}

/**
 * Devuelve `true` si el usuario tiene un rol que da acceso al panel de EMPRESA.
 *
 * Por qué no basta con `AccountType`: el backend deriva `BOTH` de
 * `roles.length > 1` (`user.entity.ts` → `getAccountType()`), sin mirar QUÉ
 * roles son: cualquier segundo rol sale `BOTH` sin ser admin de su empresa, y
 * el selector le ofrecía la vista de gestor. Preguntamos por el rol real, no
 * por el contador.
 */
export const hasAdminRole = (roles: string[] | undefined | null): boolean =>
	Array.isArray(roles) && (roles.includes(UserRole.ADMIN) || roles.includes(UserRole.MANAGEMENT));

/**
 * Resuelve qué tab renderizar dentro de un set, cayendo al primero cuando el
 * `activeTab` no pertenece a ese set.
 *
 * Al cambiar de vista, `viewType` y `activeTab` se actualizan en renders
 * distintos: durante ese hueco `activeTab` puede apuntar a un tab de la vista
 * anterior. Buscarlo y devolver `null` dejaba el área de contenido EN BLANCO
 * (regresión detectada en CI, run 30538501968, al introducir la transición
 * ADMIN → USER de las cuentas mixtas). Caer al primer tab del set actual hace
 * el render inmune a esa desincronización.
 */
export const pickTabOrFirst = <T extends { key: string }>(tabs: T[], activeTab: string): T | null =>
	tabs.find((tab) => tab.key === activeTab) ?? tabs[0] ?? null;
